import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../lib/api';
import { kr, dato } from '../lib/format';
import { PageHeader, Card } from '../components/ui';
import { ArrowLeft, FileCheck, Check, X, Printer, ClipboardList } from 'lucide-react';
import Kommentarer from '../components/Kommentarer';

const TilbudVis = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [t, setT] = useState(null);
  const [firma, setFirma] = useState(null);

  const last = () => Promise.all([
    api.get(`/tilbud/${id}`).then((r) => setT(r.data)),
    api.get('/firma').then((r) => setFirma(r.data)),
  ]);
  useEffect(() => { last(); /* eslint-disable-next-line */ }, [id]);

  const settStatus = async (status) => { await api.post(`/tilbud/${id}/status`, { status }); last(); };
  const konverter = async () => {
    if (!window.confirm('Konverter til faktura?')) return;
    try {
      const r = await api.post(`/tilbud/${id}/konverter`);
      nav(`/faktura/${r.data.id}`);
    } catch (e) { alert(e.response?.data?.error || 'Feil.'); }
  };

  if (!t || !firma) return <span className="fm-text-muted">Laster…</span>;

  return (
    <>
      {firma.logo && (
        <div style={{ marginBottom: 12 }}>
          <img src={firma.logo} alt="logo" style={{ maxHeight: 70, maxWidth: 240, objectFit: 'contain' }} />
        </div>
      )}
      <PageHeader
        title={`Tilbud ${t.fakturanummer}`}
        subtitle={t.kunde_navn}
        actions={<>
          <button className="btn-ghost" onClick={() => nav('/tilbud')}><ArrowLeft size={14} /> Tilbake</button>
          <button className="btn-ghost" onClick={() => settStatus('sendt')}><ClipboardList size={14} /> Merk sendt</button>
          <button className="btn-success" onClick={() => settStatus('akseptert')}><Check size={14} /> Akseptert</button>
          <button className="btn-ghost" onClick={() => settStatus('avslatt')} style={{ color: 'var(--theme-danger)' }}><X size={14} /> Avslått</button>
          <button className="btn-primary" onClick={konverter} disabled={t.konvertert_til_id}><FileCheck size={14} /> Konverter til faktura</button>
          <button className="btn-ghost" onClick={() => window.print()}><Printer size={14} /> Skriv ut</button>
        </>}
      />

      {t.konvertert_til_id && (
        <Card style={{ marginBottom: 12, background: 'var(--theme-success-bg, #16331f)' }}>
          <span className="badge badge-success">Konvertert</span>{' '}
          Tilbudet er konvertert til <Link to={`/faktura/${t.konvertert_til_id}`}>faktura</Link>.
        </Card>
      )}

      <Card style={{ marginBottom: 12 }}>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <div className="fm-text-muted" style={{ fontSize: 11, textTransform: 'uppercase', fontWeight: 600 }}>Fra</div>
            <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>{firma.navn}</div>
            {firma.adresse && <div style={{ fontSize: 12 }}>{firma.adresse}</div>}
            <div style={{ fontSize: 12 }}>{firma.postnr} {firma.sted}</div>
            {firma.orgnr && <div style={{ fontSize: 12 }}>Org.nr: {firma.orgnr}</div>}
          </div>
          <div>
            <div className="fm-text-muted" style={{ fontSize: 11, textTransform: 'uppercase', fontWeight: 600 }}>Til</div>
            <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>{t.kunde_navn}</div>
            {t.kunde_adresse && <div style={{ fontSize: 12 }}>{t.kunde_adresse}</div>}
            <div style={{ fontSize: 12 }}>{t.kunde_postnr} {t.kunde_sted}</div>
            {t.kunde_orgnr && <div style={{ fontSize: 12 }}>Org.nr: {t.kunde_orgnr}</div>}
            {t.kunde_epost && <div style={{ fontSize: 12 }}>{t.kunde_epost}</div>}
          </div>
        </div>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginTop: 16,
                                       paddingTop: 16, borderTop: '1px solid var(--theme-border)' }}>
          <div><div className="fm-text-muted" style={{ fontSize: 11 }}>Tilbudsdato</div><div style={{ fontSize: 13 }}>{dato(t.dato)}</div></div>
          <div><div className="fm-text-muted" style={{ fontSize: 11 }}>Gyldig til</div><div style={{ fontSize: 13 }}>{t.gyldig_til ? dato(t.gyldig_til) : '–'}</div></div>
          <div><div className="fm-text-muted" style={{ fontSize: 11 }}>Status</div><div style={{ marginTop: 2 }}><span className="badge badge-info">{t.tilbud_status || 'utkast'}</span></div></div>
          <div style={{ textAlign: 'right' }}><div className="fm-text-muted" style={{ fontSize: 11 }}>Total</div>
               <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--theme-accent)' }}>{kr(t.sum_ink_mva)}</div></div>
        </div>
      </Card>

      <Card style={{ padding: 0 }}>
        <table>
          <thead><tr>
            <th>Beskrivelse</th><th style={{ textAlign: 'right' }}>Antall</th>
            <th style={{ textAlign: 'right' }}>Pris</th><th style={{ textAlign: 'right' }}>MVA %</th>
            <th style={{ textAlign: 'right' }}>Eks. MVA</th><th style={{ textAlign: 'right' }}>Ink. MVA</th>
          </tr></thead>
          <tbody>
            {t.linjer.map((l) => (
              <tr key={l.id}>
                <td>{l.beskrivelse}</td>
                <td style={{ textAlign: 'right' }}>{l.antall}</td>
                <td style={{ textAlign: 'right' }}>{kr(l.enhetspris)}</td>
                <td style={{ textAlign: 'right' }}>{l.mva_sats}</td>
                <td style={{ textAlign: 'right' }}>{kr(l.belop_eks_mva)}</td>
                <td style={{ textAlign: 'right' }}>{kr(l.belop_ink_mva)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr><td colSpan={4} style={{ textAlign: 'right', fontWeight: 600 }}>Sum eks. MVA</td>
                <td colSpan={2} style={{ textAlign: 'right', fontWeight: 600 }}>{kr(t.sum_eks_mva)}</td></tr>
            <tr><td colSpan={4} style={{ textAlign: 'right', fontWeight: 600 }}>MVA</td>
                <td colSpan={2} style={{ textAlign: 'right', fontWeight: 600 }}>{kr(t.sum_mva)}</td></tr>
            <tr><td colSpan={4} style={{ textAlign: 'right', fontWeight: 700, fontSize: 13 }}>Total</td>
                <td colSpan={2} style={{ textAlign: 'right', fontWeight: 700, fontSize: 13, color: 'var(--theme-accent)' }}>{kr(t.sum_ink_mva)}</td></tr>
          </tfoot>
        </table>
      </Card>

      {t.notater && (
        <Card style={{ marginTop: 12 }}>
          <h3 style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 600 }}>Notater</h3>
          <p style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>{t.notater}</p>
        </Card>
      )}

      <Kommentarer entitet="tilbud" entitetId={t.id} />
    </>
  );
};

export default TilbudVis;
